import { Injectable, Injector, NgModuleFactory, NgModuleRef } from '@angular/core';
import { Configurator } from './configurator';
import { ConfiguratorParams } from '@icc/shared';
import { ConfigurationType } from '@icc/shared/types';
import { ConfiguratorContext, CONFIGURATOR_CONTEXT, createConfiguratorContext } from './configurator-context';
import { ConfiguratorInstance, ConfiguratorInstanceRegistry } from './configurator-instance-registry';

/**
 * Result of creating a new configurator instance.
 * Contains all information needed to interact with the isolated configuration.
 */
export interface CreateInstanceResult {
    /** The configurator instance with moduleRef and context */
    instance: ConfiguratorInstance;
    /** The configurator definition (steps, components, etc.) */
    configurator: Configurator;
}

@Injectable()
export abstract class AbstractConfiguratorsRepositoryService {
    static configurators: Record<string, Record<number, Configurator>> = {};
    static moduleRefs: Record<string, NgModuleRef<any>> = {};
    static configServiceClasses: Record<string, any> = {};

    /** Module factories - loaded once per type (shared code) */
    static moduleFactories: Record<string, NgModuleFactory<any>> = {};

    /** Instance registry reference for accessing active instances */
    protected static instanceRegistry: ConfiguratorInstanceRegistry | null = null;

    /**
     * Set the instance registry reference.
     * Called by concrete implementation during construction.
     */
    protected static setInstanceRegistry(registry: ConfiguratorInstanceRegistry): void {
        AbstractConfiguratorsRepositoryService.instanceRegistry = registry;
    }

    static getConfigurator(type: string, id = 0) {
        if (!this.configurators[type]?.[id]) {
            throw new Error(`Configurator ${type} (${id}) is not loaded!`);
        }
        return this.configurators[type][id];
    }

    /**
     * Get the moduleRef for a configurator type.
     * Prefers active instance's moduleRef, falls back to legacy moduleRefs.
     */
    static getModuleRef(type: string) {
        // First, check if there's an active instance of this type
        if (this.instanceRegistry) {
            const instances = this.instanceRegistry.getInstancesByType(type as ConfigurationType);
            if (instances.length > 0) {
                // Return the most recently created instance's moduleRef
                const activeInstance = this.instanceRegistry.getActiveInstance();
                if (activeInstance && activeInstance.type === type) {
                    return activeInstance.moduleRef;
                }
                // Otherwise return the first instance of this type
                return instances[0].moduleRef;
            }
        }

        // Fall back to legacy moduleRefs
        if (!this.moduleRefs[type]) {
            throw new Error(`Configurator ${type} is not loaded!`);
        }
        return this.moduleRefs[type];
    }

    static getName(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.name && configurator.parent) {
            return configurator.parent.name();
        }
        return configurator.name();
    }

    static getStepsComponents(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.stepsComponents && configurator.parent) {
            return configurator.parent.stepsComponents;
        }
        return configurator.stepsComponents;
    }

    static getSteps(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.stepsProvider && configurator.parent) {
            return configurator.parent.stepsProvider;
        }
        return configurator.stepsProvider;
    }

    static getDataLoader(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.dataLoader && configurator.parent) {
            return configurator.parent.dataLoader;
        }
        return configurator.dataLoader;
    }

    static getGroupEdit(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.groupEdit && configurator.parent) {
            return configurator.parent.groupEdit;
        }
        return configurator.groupEdit;
    }

    static getOnInit(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.onInit && configurator.parent) {
            return configurator.parent.onInit;
        }
        return configurator.onInit;
    }
    static getConfigurations(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);

        if (!configurator.configurations && configurator.parent) {
            return configurator.parent.configurations;
        }
        return configurator.configurations;
    }

    static getConfiguratorOptions(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.configuratorOptions && configurator.parent) {
            return configurator.parent.configuratorOptions;
        }
        return configurator.configuratorOptions;
    }

    static hasAdvancedDetails(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        return configurator.hasAdvancedDetails;
    }

    static getDetailsTree(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.getDetailsTree && configurator.parent) {
            return configurator.parent.getDetailsTree;
        }
        return configurator.getDetailsTree;
    }

    static getPositionTitle(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.getPositionTitle && configurator.parent) {
            return configurator.parent.getPositionTitle;
        }
        return configurator.getPositionTitle;
    }

    static getSpareParts(type: string, id?: number) {
        const configurator = this.getConfigurator(type, id);
        if (!configurator.spareParts && configurator.parent) {
            return configurator.parent.spareParts;
        }
        return configurator.spareParts;
    }

    // ============================================
    // Abstract methods
    // ============================================

    /**
     * Load configurator using shared moduleRef (legacy behavior).
     * Kept for backward compatibility during migration.
     */
    abstract loadConfigurator(type: string, id: number, params: ConfiguratorParams): Promise<void>;

    /**
     * Load module factory - ONCE per type (preserves code splitting).
     * Must be implemented by concrete service.
     */
    abstract loadModuleFactory(type: ConfigurationType): Promise<NgModuleFactory<any>>;

    /**
     * Create a new isolated configurator instance.
     * Each instance gets its own moduleRef with fresh service instances.
     *
     * @param type - The configurator type (e.g., 'external_venetian', 'roller_shutter')
     * @param id - The configurator definition ID
     * @param params - Configuration parameters
     * @param configurationId - Optional configuration ID (for editing existing)
     * @returns Promise with the instance and configurator definition
     */
    abstract createInstance(
        type: ConfigurationType,
        id: number,
        params: ConfiguratorParams,
        configurationId?: string | null
    ): Promise<CreateInstanceResult>;

    /**
     * Destroy an instance and clean up resources.
     * Should be called when a configurator is closed.
     */
    abstract destroyInstance(instanceId: string): void;

    /**
     * Get an instance by its ID.
     */
    abstract getInstance(instanceId: string): ConfiguratorInstance | undefined;

    /**
     * Get all instances of a specific configurator type.
     */
    abstract getInstancesByType(type: ConfigurationType): ConfiguratorInstance[];
}
